/**
 * HTTP client on ofetch exposing the axios surface the app was written
 * against — `get/post/put/patch/delete/request`, `{ data, status, headers }`
 * responses, `error.response.{status,data}` failures, `defaults.headers.common`
 * — plus `route()`/`formRoute()` for Wayfinder helpers. `headers` is the native
 * `Headers`: read it with `.get()`, not by index. New code can move to plain
 * `ofetch` usage.
 */
import { createFetch, type FetchOptions, type ResponseType } from 'ofetch'
import { addRouteCall } from '@u/routeCall'
import { serializeParams } from './serializeParams'
import { toFormData } from './toFormData'

export type HttpRequestConfig = {
  url?: string
  method?: string
  params?: Record<string, unknown> | URLSearchParams
  data?: unknown
  headers?: Record<string, string>
  signal?: AbortSignal
  timeout?: number
  responseType?: 'json' | 'text' | 'blob' | 'arraybuffer'
  /** Read by the apiClient error hook: the caller shows validation errors. */
  skipValidationToast?: boolean
  /** Read by the apiClient error hook: the caller handles a 404. */
  skipNotFoundToast?: boolean
  /** Read by the apiClient error hook: the caller shows its own 403 message. */
  skipForbiddenToast?: boolean
  /** Read by the apiClient error hook: the caller shows its own 409 message. */
  skipConflictToast?: boolean
}

export type HttpResponse<T = any> = {
  data: T
  status: number
  headers: Headers
  config: HttpRequestConfig
}

export type HttpErrorCode =
  | 'ERR_CANCELED'
  | 'ECONNABORTED'
  | 'ERR_NETWORK'
  | 'ERR_BAD_REQUEST'
  | 'ERR_BAD_RESPONSE'

export type HttpError = Error & {
  code: HttpErrorCode
  config: HttpRequestConfig & { headers: Record<string, string> }
  response?: { status: number; data: any; headers: Headers }
}

type RequestHook = (
  config: HttpRequestConfig & { headers: Record<string, string> }
) => void

/** Like an axios response interceptor's error handler: reject to fail, return to resolve. */
type ResponseErrorHook = (error: HttpError) => unknown

export type HttpClientOptions = {
  baseURL: string
  onRequest?: RequestHook
  onResponseError?: ResponseErrorHook
  /** Injectable for tests; defaults to the global fetch. */
  fetch?: typeof fetch
}

type RouteDefinition = { url: string; method: string }

const RESPONSE_TYPES: Record<string, ResponseType> = {
  json: 'json',
  text: 'text',
  blob: 'blob',
  arraybuffer: 'arrayBuffer'
}

function withParams(url: string, params: HttpRequestConfig['params']): string {
  const query = serializeParams(params)

  if (!query) {
    return url
  }

  return `${url}${url.includes('?') ? '&' : '?'}${query}`
}

function isMultipart(headers: Record<string, string>): boolean {
  return Object.entries(headers).some(
    ([name, value]) =>
      name.toLowerCase() === 'content-type' &&
      String(value).toLowerCase().startsWith('multipart/form-data')
  )
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') {
    return false
  }
  const prototype = Object.getPrototypeOf(value)

  return prototype === Object.prototype || prototype === null
}

function transportErrorCode(error: unknown): HttpErrorCode {
  const cause = (error as { cause?: { name?: string } })?.cause
  const name = cause?.name ?? (error as { name?: string })?.name

  if (name === 'AbortError') {
    return 'ERR_CANCELED'
  }

  return name === 'TimeoutError' ? 'ECONNABORTED' : 'ERR_NETWORK'
}

/**
 * ofetch reads an error body with the responseType the call asked for: a blob
 * download failing with a JSON `{ message }` would hand the error hook a Blob.
 * Decode it so the hook shows the API's message.
 */
async function errorData(data: unknown): Promise<unknown> {
  if (!(data instanceof Blob) || !data.type.includes('json')) {
    return data
  }

  try {
    return JSON.parse(await data.text())
  } catch {
    return data
  }
}

function httpError(
  message: string,
  code: HttpErrorCode,
  config: HttpError['config'],
  response?: HttpError['response']
): HttpError {
  return Object.assign(new Error(message), { code, config, response })
}

export function createHttpClient({
  baseURL,
  onRequest,
  onResponseError,
  fetch: fetchImplementation
}: HttpClientOptions) {
  const fetcher = createFetch({
    fetch:
      fetchImplementation ?? ((input, init) => globalThis.fetch(input, init))
  })
  const defaults = { headers: { common: {} as Record<string, string> } }

  async function request<T = any>(
    requestConfig: HttpRequestConfig
  ): Promise<HttpResponse<T>> {
    const config = {
      ...requestConfig,
      method: (requestConfig.method ?? 'get').toUpperCase(),
      headers: { ...defaults.headers.common, ...requestConfig.headers }
    }

    // axios turned a plain object into FormData when the call asked for multipart; keep that
    // contract, or the object would go out as JSON labelled multipart and Laravel read nothing.
    if (isMultipart(config.headers) && isPlainObject(config.data)) {
      config.data = toFormData(config.data)
    }

    if (config.data instanceof FormData) {
      // The browser must write the header itself to add the multipart boundary.
      for (const name of Object.keys(config.headers)) {
        if (name.toLowerCase() === 'content-type') {
          delete config.headers[name]
        }
      }
    }

    onRequest?.(config)

    let response
    try {
      response = await fetcher.raw(
        withParams(config.url ?? '', config.params),
        {
          baseURL,
          method: config.method,
          headers: config.headers,
          body: config.data as FetchOptions['body'],
          signal: config.signal,
          timeout: config.timeout,
          responseType: RESPONSE_TYPES[config.responseType ?? 'json'],
          retry: 0,
          ignoreResponseError: true
        }
      )
    } catch (transportError) {
      const error = httpError(
        (transportError as Error).message,
        transportErrorCode(transportError),
        config
      )

      return (
        onResponseError ? onResponseError(error) : Promise.reject(error)
      ) as Promise<HttpResponse<T>>
    }

    if (!response.ok) {
      const error = httpError(
        `Request failed with status code ${response.status}`,
        response.status < 500 ? 'ERR_BAD_REQUEST' : 'ERR_BAD_RESPONSE',
        config,
        {
          status: response.status,
          data: await errorData(response._data),
          headers: response.headers
        }
      )

      return (
        onResponseError ? onResponseError(error) : Promise.reject(error)
      ) as Promise<HttpResponse<T>>
    }

    return {
      data: response._data as T,
      status: response.status,
      headers: response.headers,
      config
    }
  }

  const withoutBody =
    (method: string) =>
    <T = any>(url: string, config: HttpRequestConfig = {}) =>
      request<T>({ ...config, url, method })

  const withBody =
    (method: string) =>
    <T = any>(url: string, data?: unknown, config: HttpRequestConfig = {}) =>
      request<T>({ ...config, url, method, data })

  const client = {
    defaults,
    request,
    get: withoutBody('get'),
    delete: withoutBody('delete'),
    head: withoutBody('head'),
    post: withBody('post'),
    put: withBody('put'),
    patch: withBody('patch')
  }

  return addRouteCall(client) as typeof client & {
    /** Call a Wayfinder helper: `route(DossierController.update.put({ dossier }), payload)`. */
    route<T = any>(
      route: RouteDefinition,
      ...args: unknown[]
    ): Promise<HttpResponse<T>>
    /** POST to a Wayfinder helper, spoofing its verb in `_method` (multipart uploads). */
    formRoute<T = any>(
      route: RouteDefinition,
      data?: FormData | Record<string, unknown>,
      config?: HttpRequestConfig
    ): Promise<HttpResponse<T>>
  }
}

export type HttpClient = ReturnType<typeof createHttpClient>
